import React, { useState, useEffect } from 'react';
import { Server, ServerStatus } from '../../models/Server';
import { serverService } from '../../services/serverService';
import axios from 'axios';
import '../../styles/admin/serverManagement.scss';

interface LiveMatch {
  id: number;
  team1Name: string;
  team2Name: string;
  team1Score: number;
  team2Score: number;
  serverId: number;
  serverName: string;
  currentMap: string;
  status: string;
}

const ServerManagement: React.FC = () => {
  const [servers, setServers] = useState<Server[]>([]);
  const [serverStatuses, setServerStatuses] = useState<Map<number, ServerStatus>>(new Map());
  const [loading, setLoading] = useState(true);
  const [selectedServer, setSelectedServer] = useState<Server | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'servers' | 'live-matches'>('servers');
  const [liveMatches, setLiveMatches] = useState<LiveMatch[]>([]);

  useEffect(() => {
    loadServers();
    loadLiveMatches(); // Load full details to show on server cards
  }, []);

  useEffect(() => {
    if (activeTab === 'live-matches') {
      loadLiveMatches(); // Refresh when switching to live matches tab
    }
  }, [activeTab]);

  const loadServers = async () => {
    try {
      setLoading(true);
      const response = await serverService.getAllServers();
      setServers(response.servers);
      
      // Load status for each server in parallel
      const statusPromises = response.servers.map(async (server) => {
        try {
          const status = await serverService.getServerStatus(server.id);
          return { serverId: server.id, status };
        } catch (err) {
          console.error(`Failed to get status for server ${server.id}:`, err);
          return {
            serverId: server.id,
            status: {
              serverId: server.id,
              isOnline: false,
              currentPlayers: 0,
              maxPlayers: 0,
              mapName: '',
              gameState: 'offline'
            }
          };
        }
      });

      // Wait for all status checks to complete
      const statusResults = await Promise.all(statusPromises);
      
      // Build the status map
      const statusMap = new Map<number, ServerStatus>();
      statusResults.forEach(({ serverId, status }) => {
        statusMap.set(serverId, status);
      });
      
      setServerStatuses(statusMap);
    } catch (err) {
      setError('Failed to load servers');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadLiveMatches = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/server/live-matches`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken') || localStorage.getItem('token')}`,
        },
      });
      
      // Transform the data to our LiveMatch interface
      const matches: LiveMatch[] = response.data.matches.map((match: any) => ({
        id: match.id,
        team1Name: match.team1_name || 'Team 1',
        team2Name: match.team2_name || 'Team 2',
        team1Score: match.team1_score || 0,
        team2Score: match.team2_score || 0,
        serverId: match.server_id,
        serverName: match.server_name || `Server ${match.server_id}`,
        currentMap: match.current_map || 'Unknown',
        status: match.paused ? 'Paused' : 'Live',
      }));
      
      setLiveMatches(matches);
    } catch (err) {
      console.error('Failed to load live matches:', err);
      // If the API call fails, set empty array
      setLiveMatches([]);
    }
  };

  const handleEdit = (server: Server) => {
    setSelectedServer(server);
    setShowEditModal(true);
  };

  const handleCreate = () => {
    setSelectedServer(null);
    setShowCreateModal(true);
  };

  const handleDelete = async (serverId: number) => {
    if (window.confirm('Are you sure you want to delete this server?')) {
      try {
        await serverService.deleteServer(serverId);
        await loadServers();
      } catch (err) {
        setError('Failed to delete server');
      }
    }
  };

  const handleSave = async (server: Server) => {
    try {
      if (server.id) {
        await serverService.updateServer(server.id, server);
      } else {
        await serverService.createServer(server);
      }
      setShowEditModal(false);
      setShowCreateModal(false);
      await loadServers();
    } catch (err) {
      setError('Failed to save server');
    }
  };

  const getStatusBadge = (status: ServerStatus | undefined) => {
    if (!status) {
      return <span className="status-badge offline">Unknown</span>;
    }
    
    switch (status.gameState) {
      case 'online':
        return <span className="status-badge online">Online</span>;
      case 'timeout':
        return <span className="status-badge offline">Offline</span>;
      case 'needs_update':
        return <span className="status-badge warning">Needs Update</span>;
      case 'error':
      default:
        return <span className="status-badge offline">Error</span>;
    }
  };

  if (loading) return <div className="server-management loading">Loading servers...</div>;

  return (
    <div className="server-management">
      <div className="page-header">
        <h1>Server Management</h1>
        <button className="btn btn-primary" onClick={handleCreate}>
          Add Server
        </button>
      </div>

      {error && (
        <div className="alert alert-error">
          {error}
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      <div className="tab-navigation">
        <button 
          className={`tab-button ${activeTab === 'servers' ? 'active' : ''}`}
          onClick={() => setActiveTab('servers')}
        >
          Servers ({servers.length})
        </button>
        <button 
          className={`tab-button ${activeTab === 'live-matches' ? 'active' : ''}`}
          onClick={() => setActiveTab('live-matches')}
        >
          Live Matches ({liveMatches.length})
        </button>
      </div>

      {activeTab === 'servers' ? (
        <div className="servers-grid">
        {servers.map((server) => {
          const status = serverStatuses.get(server.id);
          const hasLiveMatch = liveMatches.some(match => match.serverId === server.id);
          return (
            <div key={server.id} className={`server-card ${server.in_use ? 'in-use' : ''}`}>
              <div className="server-header">
                <h3>{server.display_name || server.ip_string}</h3>
                <div className="header-badges">
                  {hasLiveMatch && (
                    <span className="live-indicator" title="Live match in progress">
                      <span className="live-dot"></span> LIVE
                    </span>
                  )}
                  {getStatusBadge(status)}
                </div>
              </div>
              
              <div className="server-info">
                <div className="info-row">
                  <span className="label">IP:</span>
                  <span className="value">{server.ip_string}:{server.port}</span>
                </div>
                <div className="info-row">
                  <span className="label">Owner:</span>
                  <span className="value">{server.name}</span>
                </div>
                <div className="info-row">
                  <span className="label">GOTV Port:</span>
                  <span className="value">{server.gotv_port}</span>
                </div>
                <div className="info-row">
                  <span className="label">Public:</span>
                  <span className="value">{server.public_server ? 'Yes' : 'No'}</span>
                </div>
                <div className="info-row">
                  <span className="label">In Use:</span>
                  <span className="value">{server.in_use ? 'Yes' : 'No'}</span>
                </div>
                
                {status && status.isOnline && (
                  <>
                    <div className="info-row">
                      <span className="label">Players:</span>
                      <span className="value">{status.currentPlayers}/{status.maxPlayers}</span>
                    </div>
                    <div className="info-row">
                      <span className="label">Map:</span>
                      <span className="value">
                        {hasLiveMatch 
                          ? liveMatches.find(m => m.serverId === server.id)?.currentMap || status.mapName || 'N/A'
                          : status.mapName || 'N/A'}
                      </span>
                    </div>
                  </>
                )}
              </div>

              <div className="server-actions">
                <button className="btn btn-sm" onClick={() => handleEdit(server)}>
                  Edit
                </button>
                <button 
                  className="btn btn-sm btn-danger" 
                  onClick={() => handleDelete(server.id)}
                  disabled={server.in_use}
                >
                  Delete
                </button>
              </div>
            </div>
          );
        })}
      </div>
      ) : (
        <div className="live-matches-container">
          {liveMatches.length === 0 ? (
            <div className="no-matches">
              <p>No live matches at the moment</p>
            </div>
          ) : (
            <div className="live-matches-list">
              {liveMatches.map((match) => (
                <div
                  key={match.id}
                  className="live-match-item"
                  onClick={() => window.open(`https://get5.realxguys.dk/match/${match.id}`, '_blank')}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') window.open(`https://get5.realxguys.dk/match/${match.id}`, '_blank'); }}
                  style={{ cursor: 'pointer' }}
                  title="Click to view match details"
                >
                  <div className="match-teams">
                    <span className="team-name">{match.team1Name}</span>
                    <span className="vs">vs</span>
                    <span className="team-name">{match.team2Name}</span>
                  </div>
                  <div className="match-score">
                    <span className="score">{match.team1Score}</span>
                    <span className="score-separator">-</span>
                    <span className="score">{match.team2Score}</span>
                  </div>
                  <div className="match-info">
                    <span className="match-id">Match #{match.id}</span>
                    <span className="separator">•</span>
                    <span className="map-name">{match.currentMap}</span>
                    <span className="separator">•</span>
                    <span className="server-name">{match.serverName}</span>
                  </div>
                  <div className="match-status">
                    <span className={`status-badge ${match.status.toLowerCase()}`}>
                      {match.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {(showEditModal || showCreateModal) && (
        <ServerModal
          server={selectedServer}
          onSave={handleSave}
          onClose={() => {
            setShowEditModal(false);
            setShowCreateModal(false);
          }}
        />
      )}
    </div>
  );
};

interface ServerModalProps {
  server: Server | null;
  onSave: (server: Server) => void;
  onClose: () => void;
}

const ServerModal: React.FC<ServerModalProps> = ({ server, onSave, onClose }) => {
  const [formData, setFormData] = useState<Partial<Server>>({
    ip_string: '',
    port: 27015,
    rcon_password: '',
    display_name: '',
    public_server: false,
    name: '',
    user_id: 0,
    flag: 'DK',
    gotv_port: 27020,
    in_use: false,
    ...server
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData as Server);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' 
        ? (e.target as HTMLInputElement).checked 
        : type === 'number' 
        ? parseInt(value) 
        : value
    }));
  };

  return (
    <div className="modal-overlay" onClick={onClose} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onClose(); }}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()} role="dialog" onKeyDown={(e) => e.stopPropagation()}>
        <h2>{server ? 'Edit Server' : 'Add Server'}</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="server-display-name">Display Name</label>
            <input
              id="server-display-name"
              type="text"
              name="display_name"
              value={formData.display_name}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="server-ip-address">IP Address</label>
            <input
              id="server-ip-address"
              type="text"
              name="ip_string"
              value={formData.ip_string}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="server-port">Port</label>
              <input
                id="server-port"
                type="number"
                name="port"
                value={formData.port}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="server-gotv-port">GOTV Port</label>
              <input
                id="server-gotv-port"
                type="number"
                name="gotv_port"
                value={formData.gotv_port}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="server-rcon-password">RCON Password</label>
            <input
              id="server-rcon-password"
              type="password"
              name="rcon_password"
              value={formData.rcon_password}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="server-owner-name">Owner Name</label>
            <input
              id="server-owner-name"
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="server-country-flag">Country Flag</label>
            <select
              id="server-country-flag"
              name="flag"
              value={formData.flag}
              onChange={handleChange}
            >
              <option value="DK">Denmark</option>
              <option value="SE">Sweden</option>
              <option value="NO">Norway</option>
              <option value="FI">Finland</option>
              <option value="EU">Europe</option>
            </select>
          </div>

          <div className="form-group checkbox">
            <label>
              <input
                type="checkbox"
                name="public_server"
                checked={formData.public_server}
                onChange={handleChange}
              />
              Public Server
            </label>
          </div>

          <div className="form-actions">
            <button type="submit" className="btn btn-primary">
              {server ? 'Update' : 'Create'}
            </button>
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ServerManagement;